// Live ratio via SSE (/v1/stream). Deliberately EventSource, not a hand-
// rolled fetch+ReadableStream reader: EventSource gives us native
// auto-reconnect-on-drop for free, which is exactly the behavior
// stream.py's own docstring calls out as a reason SSE was chosen over
// WebSocket in the first place -- reimplementing reconnect logic by hand
// here would just be worse than what the platform already does.
//
// The one thing EventSource hides is WHY a connection died: a fatal HTTP
// response (401, 429, 502...) surfaces only as readyState CLOSED. SW-10:
// before redialing after such a close, probeStream() below asks the same
// URL with a plain fetch (headers only, body dropped) so a 429's
// Retry-After is honored instead of hammering a rate-limited server.

import { API_BASE_URL } from "./client";
import type { Tick } from "./types";

export type StreamStatus = "connecting" | "open" | "error" | "closed";

export interface StreamHandle {
  close(): void;
}

export function subscribeTickStream(
  bandWidthUsd: number,
  onTick: (tick: Tick) => void,
  onStatus: (status: StreamStatus) => void,
  // ADR-0013: optional session token. EventSource cannot set an
  // Authorization header, so the backend accepts the same JWT as a
  // `?token=` query parameter on this endpoint specifically. Passing null/
  // omitting it preserves the exact logged-out request. The caller
  // (main.ts) re-subscribes on login/logout so entitlement flips without a
  // page reload -- an EventSource's URL is fixed at construction.
  token?: string | null
): StreamHandle {
  onStatus("connecting");
  const source = new EventSource(streamUrl(bandWidthUsd, token));

  source.onopen = () => onStatus("open");
  source.onerror = () => {
    // EventSource retries on its own; just reflect the transient state.
    // If the underlying connection is truly gone (e.g. server down for a
    // long time) the browser keeps retrying with its own backoff and
    // readyState flips back to CONNECTING -- no fake "reconnected" claim
    // is made here, onStatus("open") only fires again once onopen
    // actually fires again.
    // EXCEPT after a fatal HTTP response (realistic case: expired JWT in
    // `?token=` -> 401): the browser gives up permanently and readyState
    // sticks at CLOSED. Surface that distinctly so the caller can rebuild
    // the stream (with a fresh token) -- no retry will ever come from here.
    onStatus(source.readyState === EventSource.CLOSED ? "closed" : "error");
  };
  source.onmessage = (ev: MessageEvent<string>) => {
    try {
      const tick = JSON.parse(ev.data) as Tick;
      onTick(tick);
    } catch {
      // Malformed event -- drop it, don't crash the stream.
    }
  };

  return {
    close(): void {
      source.close();
    },
  };
}

export function streamUrl(bandWidthUsd: number, token?: string | null): string {
  let url = `${API_BASE_URL}/v1/stream?band_width_usd=${bandWidthUsd}`;
  if (token) {
    url += `&token=${encodeURIComponent(token)}`;
  }
  return url;
}

/** SW-10: pre-redial probe after a fatal close. Resolves the ms to wait
 * before trying again: 0 = the stream can be opened now (2xx); on a 429,
 * max(Retry-After, backoffMs) -- Retry-After as delta-seconds or an
 * HTTP-date, unreadable/absent counts as 0 (cross-origin, the API must list
 * it in Access-Control-Expose-Headers for the browser to reveal it); any
 * other status or a network error = backoffMs, the existing behavior. */
export async function probeStream(url: string, backoffMs: number): Promise<number> {
  const ctl = new AbortController();
  try {
    const res = await fetch(url, { signal: ctl.signal });
    if (res.ok) return 0;
    if (res.status !== 429) return backoffMs;
    const h = res.headers.get("Retry-After") ?? "";
    const retryAfterMs = /^\d+$/.test(h) ? Number(h) * 1000 : Date.parse(h) - Date.now();
    return Math.max(retryAfterMs || 0, backoffMs);
  } catch {
    return backoffMs;
  } finally {
    ctl.abort(); // headers were all we needed; never consume the stream body
  }
}
